import React, { useRef, useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, Paper, Divider, TextField, List, ListItem, ListItemText, IconButton, Chip, CircularProgress, Alert, Avatar } from '@mui/material';
import { 
  Delete, 
  UploadFile, 
  Build, 
  Chat,
  Send,
  Description,
  Image,
  PictureAsPdf,
  Folder,
  Visibility,
  Construction
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { collection, addDoc, deleteDoc, onSnapshot, doc } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { uploadFileAndGetURL } from '../utils/documentService';
import { AuthContext } from '../contexts/AuthContext';

interface PrototypeFile {
  id: string;
  name: string;
  url: string;
  type: string;
  uploadedAt: number;
  userId: string;
  userName: string;
}

const PrototypingPhase: React.FC = () => {
  const { teamId, sprintId } = useParams<{ teamId: string; sprintId: string }>();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  // File upload
  const [files, setFiles] = useState<PrototypeFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // Comments
  const [comments, setComments] = useState<any[]>([]); // Changed to any[] to match new data structure
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // Firestore listeners
  useEffect(() => {
    if (!teamId || !sprintId) return;
    // Files
    const unsubFiles = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/prototyping_files`), snap => {
      setFiles(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as PrototypeFile)));
    });
    // Comments
    const unsubComments = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/prototyping_comments`), snap => {
      setComments(snap.docs.map(doc => doc.data() as any));
    });
    return () => {
      unsubFiles();
      unsubComments();
    };
  }, [teamId, sprintId]);

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && teamId && sprintId) {
      setFileLoading(true);
      setFileError(null);
      try {
        const filePath = `teams/${teamId}/designSprints/${sprintId}/prototyping/${Date.now()}_${file.name}`;
        const url = await uploadFileAndGetURL(file, filePath);
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/prototyping_files`), {
          name: file.name,
          url,
          type: file.type,
          uploadedAt: Date.now(),
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (err) {
        setFileError('Failed to upload file.');
      }
      setFileLoading(false);
    }
  };

  const handleDeleteFile = async (id: string) => {
    if (!teamId || !sprintId) return;
    try {
      await deleteDoc(doc(firestore, `teams/${teamId}/designSprints/${sprintId}/prototyping_files`, id));
    } catch (err) {
      setFileError('Failed to delete file.');
    }
  };

  // Comments
  const handleAddComment = async () => {
    if (newComment.trim() && teamId && sprintId) {
      setCommentLoading(true);
      setCommentError(null);
      try {
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/prototyping_comments`), {
          text: newComment.trim(),
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setNewComment('');
      } catch (err) {
        setCommentError('Failed to add comment.');
      }
      setCommentLoading(false);
    }
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image')) return <Image />;
    if (type === 'application/pdf') return <PictureAsPdf />;
    return <Description />;
  };

  const getFileTypeLabel = (type: string) => {
    if (type.startsWith('image')) return 'Image';
    if (type === 'application/pdf') return 'PDF';
    if (type.includes('fig') || type.includes('sketch') || type.includes('xd')) return 'Design';
    return 'File';
  };

  return (
    <div className="space-y-6">
      {/* Design Files Section */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
            <Build className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Attach Design Files</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Upload prototypes, wireframes, and design assets</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadFile />}
            disabled={fileLoading}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%) !important',
                color: '#ffffff !important'
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#6b7280'
              }
            }}
          >
            Upload File
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf,application/zip,.fig,.sketch,.xd"
              hidden
              onChange={handleFileUpload}
            />
          </Button>
        </div>

        {fileLoading && (
          <div className="flex items-center gap-2 text-amber-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Uploading file...</span>
          </div>
        )}
        
        {fileError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {fileError}
          </Alert>
        )}

        <div className="space-y-3">
          {files.map(file => (
            <div key={file.id} className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    {getFileIcon(file.type)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <a 
                        href={file.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-medium text-gray-900 font-['Inter'] hover:text-amber-600 transition-colors"
                      >
                        {file.name}
                      </a>
                      <Chip 
                        label={getFileTypeLabel(file.type)} 
                        size="small" 
                        sx={{ 
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.75rem',
                          height: '20px'
                        }} 
                      />
                    </div>
                    <p className="text-sm text-gray-500 font-['Inter']">by {file.userName || 'Unknown'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <IconButton
                    size="small"
                    href={file.url}
                    target="_blank"
                    sx={{
                      color: '#f59e0b',
                      '&:hover': { backgroundColor: '#fef3c7' }
                    }}
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteFile(file.id)}
                    disabled={fileLoading}
                    sx={{
                      color: '#ef4444',
                      '&:hover': { backgroundColor: '#fef2f2' }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
        </div>

        {files.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Build className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No design files uploaded yet. Start by uploading your prototypes!</p>
          </div>
        )}
      </div>

      {/* Wireframe Tool Section */}
      <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-slate-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-slate-500 rounded-xl flex items-center justify-center">
            <Construction className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Wireframe Tool (Coming Soon)</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Interactive design tool for creating wireframes</p>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="text-center">
            <Construction className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 font-['Inter'] mb-2">
              A simple drag-and-drop wireframe tool will be available here in the future.
            </p>
            <p className="text-gray-500 text-sm font-['Inter']">
              For now, please upload your wireframes as images or PDFs.
            </p>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
            <Chat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Team Discussion</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Share thoughts and collaborate</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Write a comment..."
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            size="small"
            fullWidth
            disabled={commentLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Share your thoughts..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
          />
          <Button
            variant="contained"
            onClick={handleAddComment}
            disabled={commentLoading || !newComment.trim()}
            startIcon={<Send />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%) !important',
                color: '#ffffff !important'
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#6b7280'
              }
            }}
          >
            Send
          </Button>
        </div>

        {commentLoading && (
          <div className="flex items-center gap-2 text-purple-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Sending comment...</span>
          </div>
        )}
        
        {commentError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {commentError}
          </Alert>
        )}

        <div className="space-y-3">
          {comments.map((comment, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-start gap-3">
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'purple.500' }}>
                  {(comment.userName || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <div className="flex-1">
                  <p className="text-gray-900 font-['Inter']">{comment.text}</p>
                  <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {comment.userName || 'Unknown'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {comments.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Chat className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No comments yet. Start the discussion!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrototypingPhase; 